import {useCallback, useEffect, useState} from "react";
import {
  CardCvcElement, CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
// components
import Button from "./Button";
// api
import {createPurchaseByQuote, startPurchaseByQuote} from "../api/purchaseApi";
import stripeLogo from "../assets/stripe.logo.svg";
// @interfaces
import { IPurchase, IQuote } from "../@interfaces/purchase.interface";
// styles
import styles from "../styles/pages/payment/CheckoutForm.module.css";
import { getCurrencySymbol } from "../utils/currency";
import { PaymentRequestPaymentMethodEvent } from "@stripe/stripe-js";
import QuickPayButton from "./QuickPayButton";
import {IWidgetConfiguration} from "../merchantConfig";
import Footer from "./Footer";
import { sendAnalyticsEvent } from "../utils/analytics";
import { TermsAndConditions } from "./TermsAndConditions";

interface CheckoutFormProps {
  programmeCode?: string | null;
  clientSecret: string;
  quote: IQuote;
  onCheckoutSuccess: (purchase: IPurchase) => void;
  setUserEmail: (email: string) => void;
  userEmail: string | null;
  appleGooglePayPaymentRequestData: any;
  merchantCode: string;
  language: string | null;
  widgetConfiguration: IWidgetConfiguration;
}

export const STRIPE_ELEMENTS_CONFIG = {
  fonts: [{
    family: 'ObjektivMk1Rg',
    src: 'url(https://res.cloudinary.com/carbonclick/raw/authenticated/s--kLgzf9A9--/v1689784589/ObjektivMk1Rg.ttf)',
    weight: '500'
  }]
};

const CheckoutForm: React.FC<CheckoutFormProps> = ({
  programmeCode,
  clientSecret,
  quote,
  onCheckoutSuccess,
  userEmail,
  language,
  setUserEmail,
  appleGooglePayPaymentRequestData,
  merchantCode,
  widgetConfiguration
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [paymentError, setPaymentError] = useState<any>(null);
  const [appleGooglePayPaymentRequest, setAppleGooglePayPaymentRequest] = useState<any>(null); //FOR GOOGLE/APPLE PAY
  const [email, setEmail] = useState("");
  const [receiveCommunications, setReceiveCommunications] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasPreviouslyEditedEmail, setHasPreviouslyEditedEmail] =
    useState(false);
  const [hasPreviouslyEditedCardNumber, setHasPreviouslyEditedCardNumber] =
    useState(false);
  const [hasPreviouslyEditedExpiry, setHasPreviouslyEditedExpiry] =
    useState(false);
  const [hasPreviouslyEditedCvc, setHasPreviouslyEditedCvc] = useState(false);
  const [cardError, setCardError] = useState([
    { type: "cardNumber", complete: false, msg: "Please enter a valid card number" },
    { type: "cardExpiry", complete: false, msg: "Card expiry is invalid" },
    { type: "cardCvc", complete: false, msg: "Card CVC is invalid" },
    { type: "email", complete: false, msg: "Email is invalid", invalid: false },
  ]);
  const [placeholders, setPlaceholders] = useState({
    cardNumber: "Card number",
    expiryDate: "Expiry date",
    cvvCode: "Security code",
  });

  const handleStripePaymentResponse = useCallback(async (stripeResponse: any, email: string, receiveCommunications: boolean) => {
    if (stripeResponse?.paymentIntent?.status === "succeeded") {
      let page;
      if (window.location !== window.parent.location && document.referrer) {
        const referrerUrl = new URL(document.referrer); // document.referrer never contains query string
        referrerUrl.search = document.location.search;  // so we use search from iframe url
        page = referrerUrl.href;
      } else {
        page = document.location.href;
      }
      const purchaseData = {
        quoteId: quote.quoteId,
        emailAddress: email,
        programmeCode: programmeCode,
        paymentIntentId: stripeResponse.paymentIntent.id,
        paymentProvider: "STRIPE",
        paymentProviderToken: null,
        metadata: receiveCommunications ? {
          merchant_code: merchantCode,
          receive_communications: receiveCommunications,
          acquisition: { page }
        } : null,
        ownershipType: 'CUSTOMER'
      };

      const confirmPaymentApiResponse = await createPurchaseByQuote(purchaseData, language);

      if (confirmPaymentApiResponse?.data?.purchaseId) {
        setIsLoading(false);
        onCheckoutSuccess(confirmPaymentApiResponse.data);
      }
    }
  }, [merchantCode, onCheckoutSuccess, quote, language, programmeCode]);


  const setLoadingAndClearPaymentError = () => {
    setIsLoading(true);
    setPaymentError(null);
  };

  const handleAppleGooglePayTriggered = useCallback(async (event: PaymentRequestPaymentMethodEvent, receiveCommunications: boolean) => {
    if(!stripe || !event.payerEmail) return;
    setLoadingAndClearPaymentError();
    try{
      const intentResponse = await startPurchaseByQuote({quoteId: quote.quoteId});

      let confirmResponse = await stripe.confirmCardPayment(
          intentResponse.data.clientSecret,
          {payment_method: event.paymentMethod.id},
          {handleActions: false}
      )

      if(confirmResponse?.error?.message){
        event.complete('fail');
        setPaymentErrorAndRemoveLoading(confirmResponse.error?.message);
      }
      else {
        event.complete('success'); // Done - close the browser payment method interface
        if (confirmResponse?.paymentIntent?.status === "requires_action") { //let Stripe.js handle flow if action required (3d secure?)
          confirmResponse = await stripe.confirmCardPayment(intentResponse.data.clientSecret);
          if (confirmResponse.error) {
            setPaymentErrorAndRemoveLoading(confirmResponse.error?.message || "There was a problem with your payment, please try again");
          } else {
            handleStripePaymentResponse(confirmResponse, event.payerEmail, receiveCommunications);
          }
        } else {
          handleStripePaymentResponse(confirmResponse,event.payerEmail, receiveCommunications);
        }
      }
    }catch(e){
      event?.complete('fail');
      setPaymentErrorAndRemoveLoading('An error occured, please try a different payment method');
    }
  }, [quote, handleStripePaymentResponse, stripe]);

  const updatePaymentDetails = useCallback(() => {
    if(stripe && appleGooglePayPaymentRequestData){
      setAppleGooglePayPaymentRequest(null);
      const pr = stripe.paymentRequest(appleGooglePayPaymentRequestData);
      pr.canMakePayment().then(result => {
        if (result) {
          pr.on('paymentmethod', (event) => handleAppleGooglePayTriggered(event, receiveCommunications));
          setAppleGooglePayPaymentRequest(pr);
        }
      });
    }
  }, [stripe, appleGooglePayPaymentRequestData, receiveCommunications, handleAppleGooglePayTriggered]);

  useEffect(() => {
    updatePaymentDetails();
  }, [updatePaymentDetails]);

  const handleChange = useCallback((card: any) => {
    setPaymentError(null);
    let error = [...cardError];

    if (card.elementType === "cardNumber") {
      error[0].complete = card.complete;
      setCardError(error);
    } else if (card.elementType === "cardExpiry") {
      error[1].complete = card.complete;
      setCardError(error);
    } else if (card.elementType === "cardCvc") {
      error[2].complete = card.complete;
      setCardError(error);
    } else if (card.elementType === "email") {
      error[3].complete = card.complete;
      error[3].invalid = card.invalid;
      setCardError(error);
    }
  }, [cardError]);

  const handleEmailChangeValue = useCallback((value: string) => {
    let complete = false;
    let invalid = false;
    if (value.match(/^([\w.%+-]+)@([\w-]+\.)+([\w]{2,})$/i)) {
      complete = true;
    } else {
      invalid = true;
    }
    setEmail(value);
    handleChange({
      elementType: "email",
      complete: complete,
      invalid: invalid,
    });
    setHasPreviouslyEditedEmail(true);
  }, [handleChange]);

  useEffect(() => {
    if(email.length === 0 && userEmail) {
      handleEmailChangeValue(userEmail || "");
    }
  }, [userEmail, email, handleEmailChangeValue])

  const handleChangeReceiveCommunications = () => {
    setIsLoading(true);
    setReceiveCommunications(!receiveCommunications);
    updatePaymentDetails();
    setIsLoading(false);
  };

  const validateCardDetails = () => {
    const result = cardError.map((card) => (card.complete === false ? 1 : 0));
    return result.reduce((acc: any, currVal) => {
      return acc + currVal;
    }, 0);
  };

  const setPaymentErrorAndRemoveLoading = (errorText: string) => {
    setIsLoading(false);
    setPaymentError({ message: errorText });
  };

  const handleEmailChange = (event: any) => {
    let complete = false;
    let invalid = false;
    if (event.target.value.match(/^([\w.%+-]+)@([\w-]+\.)+([\w]{2,})$/i)) {
      complete = true;
    } else {
      invalid = true;
    }
    setEmail(event.target.value);
    handleChange({
      elementType: "email",
      complete: complete,
      invalid: invalid,
    });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    setUserEmail(email);

    setIsLoading(true);

    sendAnalyticsEvent('add_payment_info', { co2Kgs: quote.carbonOffsetQuantity.value.toString(), itemName: "Contribution", price: quote.offsetPrice });

    let confirmResponse = await stripe.confirmCardPayment(
      clientSecret,
      {
        payment_method: {
          // @ts-ignore
          card: elements.getElement('cardNumber'),
          billing_details: {
            email,
          },
        },
      },
      { handleActions: false }
    );

    if (confirmResponse?.paymentIntent?.status === "requires_action") {
      confirmResponse = await stripe.confirmCardPayment(clientSecret);

      if (confirmResponse.error) {
        setPaymentErrorAndRemoveLoading(confirmResponse.error?.message || "There was a problem with your payment, please try again");
      } else {
        handleStripePaymentResponse(confirmResponse, email, receiveCommunications);
      }
    } else {
      handleStripePaymentResponse(confirmResponse, email, receiveCommunications);
    }

    if (confirmResponse?.error) {
      setPaymentErrorAndRemoveLoading(
        confirmResponse?.error?.message || "Something went wrong"
      );
    }
  };

  const handleCardElementReady = () => {
    setIsLoading(false);
  };

  const errorColor = "#FF4842";

  useEffect(() => {
    Promise.all([
      // @ts-ignore
      Weglot.translate({ words: [{ t: 1, w: "Card Number" }], languageTo: language }, (data) => data),
      // @ts-ignore
      Weglot.translate({ words: [{ t: 1, w: "Expiry Date" }], languageTo: language }, (data) => data),
      // @ts-ignore
      Weglot.translate({ words: [{ t: 1, w: "Security code" }], languageTo: language }, (data) => data),
    ]).then((values: string[]) => {
      const [cardNumber, expiryDate, cvvCode] = values.flatMap(e => e);
      setPlaceholders({
        cardNumber,
        expiryDate,
        cvvCode
      })
    })
  }, [language]);

  const createOptions = (placeholder: string) => {
    return {
      hideIcon: true,
      hidePostalCode: true,
      placeholder,
      style: {
        base: {
          color: '#2b283b',
          fontWeight: '500',
          fontFamily: 'ObjektivMk1Rg',
          fontSize: '16px',
          fontSmoothing: 'antialiased',
          ':-webkit-autofill': {
            color: '#2b283b',
            background: '#fff'
          },
          '::placeholder': {
            fontFamily: 'ObjektivMk1Rg',
            color: '#C2BFBF',
          },
        },
        invalid: {
          color: errorColor,
        },
      },
    };
  };

  const baseBorderStyle = "1px solid #C2BFBF";
  const invalidBorderStyle = `2px solid ${errorColor}`;

  const emailErrMessage = hasPreviouslyEditedEmail && !cardError[3].complete && cardError[3].msg;
  const cardNumberErrMessage = hasPreviouslyEditedCardNumber && !cardError[0].complete && cardError[0].msg;
  const expiryErrMessage = hasPreviouslyEditedExpiry && !cardError[1].complete && cardError[1].msg;
  const cvcErrMessage = hasPreviouslyEditedCvc && !cardError[2].complete && cardError[2].msg;

  return (
    <>
    <form
      id="stripePaymentForm"
      className={styles.stripePaymentForm}
      onSubmit={handleSubmit}
    >
      <div>
        <div className={styles.gap}>
          {appleGooglePayPaymentRequest ?
              <QuickPayButton appleGooglePayPaymentRequest={appleGooglePayPaymentRequest} />
              : <div style={{height: '40px'}}/>
          }

          <div>
            <div
              className={styles.inputWrapper}
              style={{
                border: emailErrMessage ? invalidBorderStyle : baseBorderStyle,
              }}
            >
              <input
                id="email-element"
                type="email"
                autoComplete="email"
                value={email}
                placeholder="Email address"
                name="email"
                className="cardInput"
                onChange={handleEmailChange}
                onBlur={() => setHasPreviouslyEditedEmail(true)}
                style={{
                  ...(!cardError[3].complete && hasPreviouslyEditedEmail
                    ? { color: errorColor }
                    : null),
                }}
              />
            </div>
          </div>

          <div>
            <div className={styles.inputWrapper}
              style={{
                border: cardNumberErrMessage ? invalidBorderStyle : baseBorderStyle,
              }}
            >
              <CardNumberElement
                onChange={handleChange}
                onReady={handleCardElementReady}
                options={createOptions(placeholders.cardNumber)}
                onBlur={() => setHasPreviouslyEditedCardNumber(true)}
              />
            </div>
          </div>

          <div>
          <div style={{ display: "flex", gap: "1.5rem" }}>
            <div style={{ width: "50%" }}>
              <div
                className={styles.inputWrapper}
                style={{
                  border: expiryErrMessage ? invalidBorderStyle : baseBorderStyle,
                }}
              >
                <CardExpiryElement
                  onChange={handleChange}
                  options={createOptions(placeholders.expiryDate)}
                  onBlur={() => setHasPreviouslyEditedExpiry(true)}
                />
              </div>
            </div>

            <div style={{ width: "50%" }}>
              <div
                className={styles.inputWrapper}
                style={{ border: cvcErrMessage ? invalidBorderStyle : baseBorderStyle }}
              >
                <CardCvcElement
                  onChange={handleChange}
                  options={createOptions(placeholders.cvvCode)}
                  onBlur={() => setHasPreviouslyEditedCvc(true)}
                />
              </div>
            </div>
          </div>

          <div className={styles.stripeMessage}>
            <p>100% Secure payments provided by</p>
            <img
              style={{ height: 22, width: "auto", paddingBottom: 2 }}
              src={stripeLogo}
              alt="stripe payment logo"
            />
          </div>
        </div>
        </div>

        <div style={{ color: '#ff4842', fontSize: 'var(--text-size-body-2)', textAlign: 'right' }}>
          {paymentError?.message && paymentError.message}
        </div>

        {widgetConfiguration.showMarketingConsent && widgetConfiguration.marketingConsentText && <div className={styles.checkboxContainer}>
          <label>
            <input
                checked={receiveCommunications}
                onChange={handleChangeReceiveCommunications}
                style={{marginTop: '3px', borderColor: '#999594 !important', width: '18px', height: '18px'}}
                className={styles.checkbox}
                type="checkbox"
            />
          </label>
          <p style={{fontSize: 'var(--text-size-body-2)', color: '#322A29', textAlign: 'left', marginLeft: '12px'}}>
            {widgetConfiguration.marketingConsentText}
          </p>
        </div>}
      </div>
      <div className={styles.buttonContainer}>
        <Button
          variant={"primary"}
          disabled={!(validateCardDetails() === 0 && !isLoading)}
          id="submit"
          isLoading={isLoading}
          text={`Pay ${getCurrencySymbol(quote?.offsetPrice.currency)} ${quote?.offsetPrice?.value?.toLocaleString(undefined, { 
            minimumFractionDigits: 2, maximumFractionDigits: 2
          })} ${quote?.offsetPrice?.currency}`}
        />

        <TermsAndConditions merchantCode={merchantCode} />
      </div>
      {!widgetConfiguration.hidePoweredBy && <Footer/>}

    </form>
  </>
  );
};

export default CheckoutForm;
